import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import {
  convertBefehl95FormDataToModel,
  convertModelToBefehl95FormData
} from './converters'
import Auftrag95_50Form from './Auftrag95_50Form'
import { useBefehlForm } from '../../../hooks'
import {
  BefehlFormWrapper,
  BefehlFormProps,
  BefehlFormAuftrag
} from '../Generic'
import { Befehl95FormData, createBefehl95FormValidationSchema } from './schema'
import { Befehl95 } from '../../../models'
import Auftrag95_95Form from './Auftrag95_95Form'

/**
 * Main form component for Befehl 95
 */
export default function Befehl95Form({
  befehl,
  onSubmit,
  readOnly = false,
  'data-testid': testId = 'befehl-95-form',
  activateEmptyFieldWarnings = false,
  displayHeaderOnly = false
}: Readonly<BefehlFormProps<Befehl95>>) {
  const { t } = useTranslation('ui-befehle')

  const validationSchema = useMemo(
    () => createBefehl95FormValidationSchema(),
    []
  )

  const formMethods = useBefehlForm({
    readOnly,
    activateEmptyFieldWarnings,
    schema: validationSchema,
    initialData: befehl ? convertModelToBefehl95FormData(befehl) : undefined
  })

  const handleSubmit = useCallback(
    (data: Befehl95FormData) => {
      const updatedBefehl = convertBefehl95FormDataToModel(
        data,
        befehl.frontendId
      )
      onSubmit(updatedBefehl)
    },
    [befehl.frontendId, onSubmit]
  )

  return (
    <BefehlFormWrapper<Befehl95FormData>
      befehlKennung="95"
      title={t('befehle.95.description')}
      formMethods={formMethods}
      onSubmit={handleSubmit}
      readOnly={readOnly}
      data-testid={`${testId}-wrapper`}
      activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      displayHeaderOnly={displayHeaderOnly}
    >
      <BefehlFormAuftrag
        auftragKennung={`95.10`}
        fieldName={'auftrag_95_10.enabled'}
        readOnly={readOnly}
        testId={`${testId}-auftrag-95-10`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      >
        <div
          className="flex items-baseline gap-2"
          data-testid={`${testId}-auftrag-95-10-text`}
        >
          {t('auftraege.95_10')}
        </div>
      </BefehlFormAuftrag>

      <BefehlFormAuftrag
        auftragKennung={`95.20`}
        fieldName={'auftrag_95_20.enabled'}
        readOnly={readOnly}
        testId={`${testId}-auftrag-95-20`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      >
        <div
          className="flex items-baseline gap-2"
          data-testid={`${testId}-auftrag-95-20-text`}
        >
          {t('auftraege.95_20')}
        </div>
      </BefehlFormAuftrag>

      <BefehlFormAuftrag
        auftragKennung={`95.30`}
        fieldName={'auftrag_95_30.enabled'}
        readOnly={readOnly}
        testId={`${testId}-auftrag-95-30`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      >
        <div
          className="flex items-baseline gap-2"
          data-testid={`${testId}-auftrag-95-30-text`}
        >
          {t('auftraege.95_30')}
        </div>
      </BefehlFormAuftrag>

      <BefehlFormAuftrag
        auftragKennung={`95.40`}
        fieldName={'auftrag_95_40.enabled'}
        readOnly={readOnly}
        testId={`${testId}-auftrag-95-40`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      >
        <div
          className="flex items-baseline gap-2"
          data-testid={`${testId}-auftrag-95-40-text`}
        >
          {t('auftraege.95_40')}
        </div>
      </BefehlFormAuftrag>

      {/* Auftrag 95.50 */}
      <Auftrag95_50Form
        readOnly={readOnly}
        data-testid={`${testId}-auftrag-95-50`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      />

      <Auftrag95_95Form
        fieldNamePrefix="auftrag_95_95"
        readOnly={readOnly}
        data-testid={`${testId}-auftrag-95-95`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      />
    </BefehlFormWrapper>
  )
}
